import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { KeyRound } from "lucide-react";

export default function ChangePasswordCard() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) {
      toast.error("New password must be at least 6 characters.");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated.");
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!open) {
    return (
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-slate-100 flex items-center justify-center">
            <KeyRound size={15} className="text-slate-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900">Password</div>
            <div className="text-xs text-slate-500">Change the password you use to sign in.</div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full border-slate-300"
          onClick={() => setOpen(true)}
          data-testid="change-password-open-btn"
        >
          Change
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="change-password-form">
      <div className="text-sm font-semibold text-slate-900">Change password</div>
      <div>
        <Label htmlFor="cp-current" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
          Current password
        </Label>
        <Input
          id="cp-current"
          type="password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
          className="mt-2 rounded-2xl border-slate-200 bg-white"
          data-testid="cp-current-input"
        />
      </div>
      <div>
        <Label htmlFor="cp-new" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
          New password
        </Label>
        <Input
          id="cp-new"
          type="password"
          required
          minLength={6}
          value={next}
          onChange={(e) => setNext(e.target.value)}
          className="mt-2 rounded-2xl border-slate-200 bg-white"
          placeholder="At least 6 characters"
          data-testid="cp-new-input"
        />
      </div>
      <div>
        <Label htmlFor="cp-confirm" className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">
          Confirm new password
        </Label>
        <Input
          id="cp-confirm"
          type="password"
          required
          minLength={6}
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="mt-2 rounded-2xl border-slate-200 bg-white"
          data-testid="cp-confirm-input"
        />
      </div>
      <div className="flex items-center gap-2 pt-1">
        <Button
          type="submit"
          disabled={loading}
          className="rounded-full bg-slate-900 hover:bg-slate-800 text-white"
          data-testid="cp-submit-btn"
        >
          {loading ? "Updating…" : "Update password"}
        </Button>
        <Button
          type="button"
          variant="ghost"
          className="rounded-full text-slate-600"
          onClick={() => { reset(); setOpen(false); }}
          data-testid="cp-cancel-btn"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
}
